<template>
  <div
    id="tabControl"
    class="row relative btn-row pt-7 mx-0 flex-nowrap overflow-auto w-100"
  >
    <button
      type="button"
      class="tab-btn font-family-secondary mb-0 py-2 px-4 tab-control-btn text-primary"
      :class="{ active: activeTab === 1 }"
      @click="
        () => {
          emit('changeActiveTab', 1)
        }
      "
    >
      {{ t('description') }}
    </button>
    <button
      type="button"
      class="tab-btn font-family-secondary mb-0 py-2 px-4 tab-control-btn text-primary"
      :class="{ active: activeTab === 2 }"
      @click="
        () => {
          emit('changeActiveTab', 2)
        }
      "
    >
      {{ t('composition') }} & {{ t('nutritional_information') }}
    </button>
    <button
      type="button"
      class="tab-btn font-family-secondary mb-0 py-2 px-4 tab-control-btn text-primary"
      :class="{ active: activeTab === 3 }"
      @click="
        () => {
          emit('changeActiveTab', 3)
        }
      "
    >
      {{ t('ratings') }}
      <span v-if="commentsSum > 0">({{ commentsSum }})</span>
    </button>
  </div>
  <div class="tab-control-tabs relative zindex-2 md:pl-5 md:pr-5 pl-0 pr-0">
    <div class="container mx-auto md:pt-6 pt-5 pb-6 lg:pl-0 pl-4 lg:pr-0 pr-4">
      <div
        v-if="activeTab === 1"
        class="tab"
      >
        <div class="row mx-0">
          <!-- Description + ean -->
          <div class="lg:col-6 col-12 pr-0 lg:pr-3 pl-0">
            <div class="text-sm">
              <p
                v-if="product.state === 'weather_too_hot'"
                class="font-bold text-info"
              >
                {{ t('selected_chocolate_products') }}
              </p>
              <p v-html="product.description" />
              <p
                v-if="product.tags"
                class="tags-row"
              >
                <em
                  v-for="(tag, index) in product.tags.split(', ')"
                  :key="index"
                  class="mr-1 inline-block"
                >
                  #{{ tag }}
                </em>
              </p>
            </div>
          </div>
          <div class="lg:col-6 col-12 pl-0 lg:pl-3 pr-0 mt-5 lg:mt-0">
            <ProductBenefitsList
              v-if="product.isReadyProduct"
              :benefits="productBenefits"
            />
          </div>
        </div>
      </div>
      <div
        v-if="activeTab === 2"
        class="tab"
      >
        <div class="row mx-0">
          <div class="lg:col-6 col-12 pr-0 lg:pr-3 pl-0">
            <ProductComposition
              :product="product"
              :with-title="false"
              :show-full-composition="true"
            />
          </div>
          <div class="lg:col-6 col-12 mt-5 lg:mt-0 pl-0 lg:pl-3 pr-0">
            <ProductPack
              v-if="product.productPack"
              :product="product"
            />
            <ProductNutritionTable
              v-if="product.showNutrition"
              :product="product"
              :is-full-width="true"
              :theme-light="true"
              section-classes="pt-0"
            />
          </div>
        </div>
      </div>
      <div
        v-if="activeTab === 3"
        class="tab"
      >
        <ProductReviews
          v-if="topComments && comments"
          :product="product"
          :top-comments="topComments"
          :comments="comments"
        />
        <BaseLoading
          v-else
          :style="{ minHeight: '490px' }"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import ProductBenefitsList from '@/components/products/ProductBenefitsList.vue'
import ProductComposition from '@/components/products/ProductComposition.vue'
import ProductNutritionTable from '@/components/products/ProductNutritionTable.vue'
import ProductPack from '@/components/products/ProductPack.vue'
import ProductReviews from '@/components/products/ProductReviews.vue'
import BaseLoading from '@/components/general/BaseLoading.vue'

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  activeTab: {
    type: Number,
    required: true,
  },
  commentsSum: {
    type: Number,
    required: true,
  },
  topComments: {
    type: Array,
    required: true,
    default: () => [],
  },
  comments: {
    type: Array,
    required: true,
    default: () => [],
  },
})

const emit = defineEmits(['changeActiveTab'])

const productBenefits = computed(() => {
  return props.product ? props.product.properties.filter((p) => p.propertyType === 'benefit') : []
})

const { t } = useI18n()
</script>

<style lang="scss">
.tab-control-btn {
  opacity: 0.7;
  border: none;
  outline: none;
  background-color: rgba(0, 0, 0, 0);
  border-top-left-radius: 22px;
  border-top-right-radius: 22px;
  box-shadow: 0px -2px 4px rgba(0, 0, 0, 0);
  transition: 0.5s box-shadow;

  &.active {
    opacity: 1;
    box-shadow: 0px -2px 4px rgba(0, 0, 0, 0.1);
    background-color: #f7e7d1;
  }
}
.tab-control-tabs {
  box-shadow: 0px -6px 7px rgba(0, 0, 0, 0.1);
}

.btn-row {
  z-index: 4;
  justify-content: center;
}

.tab-btn {
  font-weight: 800;
  font-size: 20px;
}

@media screen and (max-width: 991px) {
  .tab-control-btn {
    &.active {
      background-color: $white;
    }
  }
}

@media screen and (max-width: 650px) {
  .btn-row {
    justify-content: flex-start;
    padding-left: 6px;
    padding-right: 6px;

    &::-webkit-scrollbar {
      height: 4px;
    }
    &::-webkit-scrollbar-track {
      background: $white;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(79, 43, 27, 0.4);
      border-radius: 50rem;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: $primary-color-800;
    }
  }
  .tab-btn {
    font-size: 16px;
  }
}
</style>

<i18n>
  {
    "cs": {
    "composition": "Složení",
    "description": "Popis",
    "nutritional_information": "Výživové údaje",
    "ratings": "Hodnocení",
    "selected_chocolate_products": "Jsem u ledu! Z důvodu vysokých teplot mě poslali do chládku."
},
    "sk": {
    "composition": "Zloženie",
    "description": "Popis",
    "nutritional_information": "Výživové údaje",
    "ratings": "Hodnotenie",
    "selected_chocolate_products": "Som v chládku! :( Čokoláda má kvôli vysokým teplotám dovolenku."
},
    "pl": {
    "composition": "Skład",
    "description": "Opis",
    "nutritional_information": "Wartości odżywcze",
    "ratings": "Ocena",
    "selected_chocolate_products": "Czekam w chłodzie! :( Ze względu na upały, czekolada została wysłana na przymusowy urlop."
},
    "hu": {
    "composition": "Összetevők",
    "description": "Leírás",
    "nutritional_information": "Táplálkozási adatok",
    "ratings": "Értékelés",
    "selected_chocolate_products": "Hidegre helyezve! :( A magas hőmérsékletek miatt a csokoládés termékek szabadságon vannak."
},
    "de": {
    "composition": "Zusammensetzung:",
    "description": "Beschreibung",
    "nutritional_information": "Nährwertangaben",
    "ratings": "Bewertung",
    "selected_chocolate_products": "Ich liege auf Eis! :( Wegen der hohen Temperaturen haben sie mich in die Kühlung geschickt."
},
    "at": {
    "composition": "Zusammensetzung:",
    "description": "Beschreibung",
    "nutritional_information": "Nährwertangaben",
    "ratings": "Bewertung",
    "selected_chocolate_products": "Ich liege auf Eis! :( Wegen der hohen Temperaturen haben sie mich in die Kühlung geschickt."
},
    "ro": {
    "composition": "Ingrediente",
    "description": "Descriere",
    "nutritional_information": "Informații nutriționale",
    "ratings": "Evaluare",
    "selected_chocolate_products": "La rece! :( Din cauza temperaturilor înalte produsele cu ciocolată sunt în concediu."
},
    "fr": {
    "composition": "Composition",
    "description": "Description",
    "nutritional_information": "Valeurs nutritionnelles",
    "ratings": "Avis des clients",
    "selected_chocolate_products": "Je suis au frais ! À cause des températures élevées, ils m'ont plongée dans le froid."
},
    "it": {
    "composition": "Ingredienti",
    "description": "Descrizione",
    "nutritional_information": "Valori nutrizionali",
    "ratings": "Recensione",
    "selected_chocolate_products": "Sono sotto ghiaccio! A causa delle alte temperature mi hanno mandato in spedizione polare per raffreddarmi."
}
  }
</i18n>
